import React, { useEffect, useState } from "react";
import { createInvites, checkInviteEmailAccount } from "../api/invitesAPI";
import { getAll } from "../api";
import { Icon } from "@iconify/react/dist/iconify.js";
import { showToastWarning } from "../utils/errorController";

const InviteUsersModal = ({ tenantId, onClose }) => {
  const DEFAULT_ROLE_ID = 3;
  const [invitees, setInvitees] = useState([{ email: "", roleId: null }]);
  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(true);
  const [rolesError, setRolesError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let isMounted = true;

    const loadRoles = async () => {
      setRolesLoading(true);
      try {
        const data = await getAll('roles');
        if (!isMounted) {
          return;
        }
        setRoles(Array.isArray(data) ? data : []);
        setRolesError("");
      } catch (err) {
        console.error('Failed to load roles', err);
        if (!isMounted) {
          return;
        }
        setRoles([]);
        setRolesError("Unable to load roles. Please refresh and try again.");
      } finally {
        if (isMounted) {
          setRolesLoading(false);
        }
      }
    };

    loadRoles();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!rolesLoading && roles.length > 0) {
      setInvitees((prev) =>
        prev.map((entry) => ({
          ...entry,
          roleId: entry.roleId ?? roles[0].id,
        })),
      );
    }
  }, [rolesLoading, roles]);

  const updateInvitee = (index, updates) => {
    setInvitees((prev) =>
      prev.map((item, idx) => (idx === index ? { ...item, ...updates } : item)),
    );
  };

  const addEmailField = () => {
    const fallbackRoleId = roles[0]?.id ?? (rolesLoading ? null : DEFAULT_ROLE_ID);
    setInvitees((prev) => [...prev, { email: "", roleId: fallbackRoleId }]);
  };

  const removeEmailField = (index) => {
    setInvitees((prev) => prev.filter((_, idx) => idx !== index));
  };

  const resetForm = () => {
    const fallbackRoleId = roles[0]?.id ?? (rolesLoading ? null : DEFAULT_ROLE_ID);
    setInvitees([{ email: "", roleId: fallbackRoleId }]);
    setError("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setResult(null);
    const fallbackRoleId = roles[0]?.id ?? DEFAULT_ROLE_ID;
    const filtered = invitees
      .map(({ email, roleId }) => ({
        email: email.trim(),
        roleId: roleId ?? (rolesLoading ? null : fallbackRoleId),
      }))
      .filter(({ email }) => Boolean(email));
    if (!filtered.length) {
      setError("Please enter at least one email address.");
      return;
    }
    if (filtered.some(({ roleId }) => !roleId)) {
      setError("Please select a role for each invited user.");
      return;
    }
    if (rolesError) {
      setError(rolesError);
      return;
    }
    setSubmitting(true);
    try {
      const uniqueEmails = Array.from(new Set(filtered.map((item) => item.email)));
      const lookedUpEmails = await Promise.all(
        uniqueEmails.map(async (email) => {
          try {
            const result = await checkInviteEmailAccount(email);
            return {
              email,
              databaseName: result?.databaseName || null,
            };
          } catch (err) {
            console.error(`Error checking email account for ${email}`, err);
            return { email, databaseName: null };
          }
        }),
      );
      const existingEmails = lookedUpEmails
        .filter((entry) => Boolean(entry.databaseName))
        .map((entry) => entry.email);
      if (existingEmails.length > 0) {
        const emailText =
          existingEmails.length === 1 ? existingEmails[0] : existingEmails.join(', ');
        showToastWarning(
          `${emailText} already has an account in another company. The invite will still be sent.`,
        );
      }

      const data = await createInvites({
        tenantId: Number(tenantId),
        emails: filtered.map((item) => item.email),
        roleIds: filtered.map((item) => item.roleId),
      });
      setResult(data);
      resetForm();
    } catch (err) {
      const message = err.response?.data?.message || err.message || "Unable to send invites.";
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="modal fade show d-block" tabIndex="-1" role="dialog">
        <div className="modal-dialog modal-lg">
          <div className="modal-content" style={{ marginTop: "5vh" }}>
            <div className="modal-header">
              <h5 className="modal-title">Invite Users</h5>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
                aria-label="Close"
              />
            </div>
            <div className="modal-body">
              <span className="mb-3"> Enter one or more email addresses below. Each person will receive an
                invitation email with a link to sign up for <strong>Samurai</strong>.
                Once they complete registration, they’ll have access to your system.</span>
              {!result && (
                <form onSubmit={handleSubmit}>
                  {invitees.map((invitee, idx) => (
                    <div className="row g-2 align-items-start mb-3" key={`email-${idx}`}>
                      <div className="col-12 col-md-6">
                        <input
                          type="email"
                          className="form-control"
                          required
                          value={invitee.email}
                          onChange={(event) => updateInvitee(idx, { email: event.target.value })}
                          placeholder="user@example.com"
                        />
                      </div>
                      <div className="col-12 col-md-4">
                        <select
                          className="form-select"
                          value={invitee.roleId ?? ""}
                          onChange={(event) =>
                            updateInvitee(idx, { roleId: Number(event.target.value) })
                          }
                          disabled={rolesLoading || !!rolesError}
                          required
                        >
                          <option value="" disabled>
                            {rolesLoading ? "Loading roles..." : "Select role"}
                          </option>
                          {roles.map((role) => (
                            <option key={role.id} value={role.id}>
                              {role.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-12 col-md-2 d-flex align-items-center">
                        {invitees.length > 1 && (
                          <button
                            type="button"
                            className="w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center ms-3"
                            style={{ marginTop: "2px" }}
                            onClick={() => removeEmailField(idx)}
                            aria-label="Remove email"
                          >
                            <Icon icon="ic:baseline-delete-outline" className="icon text-xl line-height-1" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="btn btn-link px-0 text-decoration-none"
                    onClick={addEmailField}
                  >
                    + Add another email
                  </button>
                  {error && <div className="text-danger mt-2">{error}</div>}
                  {!error && rolesError && (
                    <div className="text-danger mt-2">{rolesError}</div>
                  )}
                  <div className="d-flex justify-content-end gap-2 mt-3">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={onClose}
                      disabled={submitting}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={submitting || rolesLoading || !!rolesError}
                    >
                      {submitting ? "Sending..." : "Send Invites"}
                    </button>
                  </div>
                </form>
              )}
              {result && (
                <div>
                  <ul className="list-group mb-3">
                    {result.emails?.map((item) => (
                      <li
                        key={item.email}
                        className={`list-group-item d-flex justify-content-between align-items-center`}
                      >
                        <span>{item.email}</span>
                        <span className={`badge ${item.warning ? 'bg-warning text-dark' : 'bg-primary'}`}>
                          {item.warning ? 'warning' : item.status}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <div className="d-flex align-items-center gap-2 text-success">
                    <Icon icon="mage:check-circle-fill" />
                    <div>Invites processed</div>
                  </div>
                </div>
              )}
            </div>
            {result && (
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={onClose}>
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" />
    </>
  );
};

export default InviteUsersModal;
